import React, { useState, useRef } from 'react';
import { Box, Typography, Button, Snackbar } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { getCourseImageDisplayUrlWithCleanup } from 'src/utils/imageUtils';

// Configurable dimensions - change these values to update throughout the component
const IMAGE_CONFIG = {
  MIN_WIDTH: 800,
  MIN_HEIGHT: 600,
  DISPLAY_WIDTH: 200,
  DISPLAY_HEIGHT: 150
};

const UploadCourseImage = ({ 
  course, 
  setCourse, 
  selectedImages, 
  setSelectedImages, 
  error, 
  imageDisplayData, 
  setImageDisplayData 
}) => {
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const inputRef = useRef(null);

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackBarOpen(false);
  };

  const removeSelectedImages = () => {
    // Clean up any existing blob URLs
    imageDisplayData.cleanup();
    
    setSelectedImages([]);
    setCourse((prevCourse) => ({
      ...prevCourse,
      image: "",
    }));

    // Reset the file input
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setSnackBarOpen(true);
      
      e.target.value = '';
      return;
    }
    
    
    if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
      setSnackBarOpen(true);
      
      e.target.value = '';
      return;
    }
    console.log('file');
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      if (img.width < IMAGE_CONFIG.MIN_WIDTH || img.height < IMAGE_CONFIG.MIN_HEIGHT) {
        setSnackBarOpen(true);
        
        e.target.value = '';
    } else {
        setCourse((prevCourse) => ({
          ...prevCourse,
          image: file,
        }));

        const reader = new FileReader();
        reader.onload = (e) => {
          setSelectedImages([{ file, url: e.target.result }]);
        };
        reader.readAsDataURL(file);
      }
    };

    img.onerror = () => {
      setSnackBarOpen(true);
      e.target.value = '';
    };
  };

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  return (
    <div style={{ marginTop: "20px", marginBottom: "20px" }}>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            fontSize: "16px",
            color: "#333",
            marginRight: '8px'
          }}
        >
          Select your cover image (recommended)
        </Typography>
        <InfoIcon 
          sx={{ 
            fontSize: '18px', 
            color: '#712d94', 
            cursor: 'pointer',
            '&:hover': { opacity: 0.7 }
          }}
          onClick={() => setShowExample(!showExample)}
        />
        <Typography
          sx={{
            fontSize: "14px",
            color: '#712d94',
            cursor: 'pointer',
            marginLeft: '4px',
            textDecoration: 'underline',
            '&:hover': { opacity: 0.7 }
          }}
          onClick={() => setShowExample(!showExample)}
        >
          Show me an example
        </Typography>
      </Box>

      {/* Description */}
      <Typography
        sx={{
          fontSize: "15px",
          color: "#666",
          marginBottom: '20px',
          lineHeight: '1.4'
        }}
      >
        Your cover image will be displayed in the search results and as the first image on your resource page in the preview function. It is important to add an eye catching cover image that gives other teachers an idea about what your resource contains.
      </Typography>

      {/* Upload Area and Preview Container */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        {/* Upload Area and Preview Row */}
        <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
          {/* Upload Area - Smaller */}
          <Box
            sx={{
              border: '2px dashed #ddd',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center',
              backgroundColor: '#f9f9f9',
              width: '250px',
              height: '200px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              flexShrink: 0
            }}
          >
            {/* Placeholder Content (only show when no image) */}
            
                {/* Gallery Icon */}
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '8px'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#666">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                </Box>

                {/* Size Requirements */}
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#666',
                    marginBottom: '2px'
                  }}
                >
                  {IMAGE_CONFIG.MIN_WIDTH} x {IMAGE_CONFIG.MIN_HEIGHT}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: '#999'
                  }}
                >
                  minimum size
                </Typography>

            {/* Hidden file input */}
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleImageChange}
              style={{ display: 'none' }}
            />
          </Box>

          {/* Image Preview - Next to upload area */}
          {(selectedImages.length > 0 || course?.image) && (
            <Box sx={{ position: 'relative' }}>
              {/* Close Icon */}
              <HighlightOffRoundedIcon
                sx={{
                  width: "20px",
                  height: "20px",
                  cursor: "pointer",
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  color: '#666',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  borderRadius: '50%',
                  zIndex: 2,
                  '&:hover': { 
                    color: '#712d94',
                    backgroundColor: 'rgba(255,255,255,1)'
                  }
                }}
                onClick={removeSelectedImages}
              />

              {/* Image Preview */}
              <img
                src={selectedImages.length > 0 ? selectedImages[0].url : imageDisplayData.url}
                alt="Preview"
                style={{
                  width: '250px',
                  height: '200px',
                  padding: '10px',
                  objectFit: "contain",
                  borderRadius: '8px',
                  border: '2px solid #ddd'
                }}
              />
            </Box>
          )}
        </Box>

        {/* Success Message - Under the boxes */}
        {(selectedImages.length > 0 || course?.image) && (
          <Typography
            sx={{
              fontSize: "14px",
              color: "#4caf50",
              fontWeight: "bold",
            }}
          >
            {selectedImages.length > 0 ? "✓ Image uploaded successfully" : "✓ Current cover image"}
          </Typography>
        )}

        {/* Upload Button - Left aligned */}
        <Button
          variant="outlined"
          onClick={handleUploadClick}
          sx={{
            borderColor: '#ccc',
            color: '#712d94',
            backgroundColor: '#fff',
            fontSize: '14px',
            padding: '8px 16px',
            minWidth: '120px',
            alignSelf: 'flex-start',
            '&:hover': {
              borderColor: '#712d94',
              backgroundColor: '#f8f5ff'
            }
          }}
        >
          Upload image
          </Button>
      </Box>

      {/* Error Display */}
      {error.image && (
        <Typography sx={{ color: "red", fontSize: "14px", marginTop: "5px" }}>
          {error.image}
        </Typography>
      )}

      {/* Error Snackbar */}
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnack}
        message={`Image must be at least ${IMAGE_CONFIG.MIN_WIDTH} x ${IMAGE_CONFIG.MIN_HEIGHT} pixels, under 5MB, and in JPEG or PNG format.`}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#f44336',
            color: 'white'
          }
        }}
      />

      {/* Enhanced Example Modal */}
      {showExample && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowExample(false)}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography sx={{ 
              fontWeight: 'bold', 
              fontSize: '18px',
              marginBottom: '20px',
              color: '#333'
            }}>
              Example Cover Image
            </Typography>
            
            {/* Example Image Container */}
            <Box
              sx={{
                width: `100%`,
                height: `${IMAGE_CONFIG.MIN_HEIGHT * 0.3}px`,
                backgroundColor: '#f0f0f0',
                border: '2px dashed #ccc',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}
            >
              {/* Example landscape icon */}
              <svg width="60" height="40" viewBox="0 0 24 24" fill="#999">
                <path d="M14,6L11,9L8,6H2V18H22V6H14M20,16H4V8H6.5L9.5,11L12.5,8H14.5L17.5,11L20,8V16Z"/>
              </svg>
              
              {/* Dimensions overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 'bold'
                }}
              >
                {IMAGE_CONFIG.MIN_WIDTH}×{IMAGE_CONFIG.MIN_HEIGHT}
              </Box>
            </Box>

            <Typography sx={{ 
              fontSize: '14px', 
              color: '#666',
              margin: '20px 0px',
              lineHeight: '1.5'
            }}>
              Your cover image should be clear, relevant to your course content, and at least {IMAGE_CONFIG.MIN_WIDTH}×{IMAGE_CONFIG.MIN_HEIGHT} pixels. 
              The image will be displayed in search results and on your course page.
            </Typography>

            <Typography sx={{ 
              fontSize: '12px', 
              color: '#999', 
              marginBottom: '20px',
              fontStyle: 'italic'
            }}>
              Recommended: Use high-quality images with good contrast and clear subject matter.
            </Typography>

            <Button
              variant="contained"
              onClick={() => setShowExample(false)}
              sx={{ 
                backgroundColor: '#712d94',
                '&:hover': { backgroundColor: '#5a1f7a' }
              }}
            >
              Got it
          </Button>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default UploadCourseImage;
